import { NgModule } from '@angular/core';
import { MatDialogModule, MatProgressSpinnerModule, MatRadioModule } from '@angular/material';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@asymmetrik/ngx-leaflet-markercluster';
import { DisclaimerModule } from '@core/components/disclaimer/disclaimer.module';
import { FcSegmentsInputModule } from '@core/components/fc-input/search-many/fc-segments-input/fc-segments-input.module';
import { FcPaginatorModule } from '@core/components/fc-paginator/fc-paginator.module';
import { FcOutlinedSelectModule } from '@core/components/fc-select/base/fc-outlined-select/fc-outlined-select.module';
import { FcMaintenanceTypesOutlinedSelectModule } from '@core/components/fc-select/search-many/fc-maintenances-types-outlined-select/fc-maintenance-types-outlined-select.module';
import { FcCompanyOutlinedSelectModule } from '@core/components/fc-select/search-one/fc-company-outlined-select/fc-company-outlined-select.module';
import { FcDeadlineContractOutlinedSelectModule } from '@core/components/fc-select/search-one/fc-deadline-contract-outlined-select/fc-deadline-contract-outlined-select.module';
import { FcSegmentOutlinedSelectModule } from '@core/components/fc-select/search-one/fc-segment-outlined-select/fc-segment-outlined-select.module';
import { AddressModule } from '@core/components/inputs/address/address.module';
import { CompaniesComponentModule } from '@core/components/inputs/companies/companies.module';
import { LocationsGroupsComponentModule } from '@core/components/inputs/locations-groups/locations-groups.module';
import { MaintenanceTypeSearchComponentModule } from '@core/components/inputs/maintenance-type-search/maintenance-type-search.module';
import { MaintenanceTypeModule } from '@core/components/inputs/maintenance-type/maintenance-type.module';
import { SegmentsModule } from '@core/components/inputs/segments/segments.module';
import { SelectWithSearchModule } from '@core/components/select-with-search/select-with-search.module';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { FuseSharedModule } from '@fuse/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { NgxLeafletFullscreenModule } from '@runette/ngx-leaflet-fullscreen';
import { NgxMaskModule } from 'ngx-mask';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ArchiveEnvironmentDialogModule } from '../../../../core/components/archive-environment-dialog/archive-environment-dialog.module';
import { ArchiveSectorDialogModule } from '../../../../core/components/archive-sector-dialog/archive-sector-dialog.module';
import { FcButtonModule } from '../../../../core/components/fc-button/fc-button.module';
import { FcTextInputModule } from '../../../../core/components/fc-input/base/fc-text-input/fc-text-input.module';
import { TreeViewModule } from '../../../../core/components/fc-tree-view/fc-tree-view.module';
import { MapAddressModule } from '../../../../core/components/map-address/map-address.module';
import { VideoInfoModalModule } from '../../../../core/components/video-info-modal/video-info-modal.module';
import { DirectivesModule } from '../../../../core/directives/directives.module';
import {
  LocationService,
  QueryAddressCoordinatesGQL,
  QueryAddressGeocodeGQL,
  QueryAddressPostalCodeGQL,
  QueryCoordinatesGeocodeGQL
} from '../../services/location.service';
import { DialogAlertModule } from '../dialog-alert/dialog-alert.module';
import { DialogLinkEquipmentModule } from '../dialog-link-equipment/dialog-link-equipment.module';
import { DialogLocationsComponent } from './dialog-locations.component';
import { TabContactsComponent } from './tabs/contacts/tab-contacts.component';
import { TabGeneralComponent } from './tabs/general/tab-general.component';
import { LinkGroupsComponent } from './tabs/groups/link-groups/link-groups.component';
import { TabGroupsComponent } from './tabs/groups/tab-groups.component';
import { TabLinksComponent } from './tabs/links/tab-links.component';
import { TabLocationComponent } from './tabs/location/tab-location.component';
import { TabNewProvidersComponent } from './tabs/new-providers/tab-new-providers.component';

@NgModule({
  entryComponents: [
    DialogLocationsComponent,
    LinkGroupsComponent
  ],
  declarations: [
    DialogLocationsComponent,
    TabGeneralComponent,
    TabLocationComponent,
    TabGroupsComponent,
    TabNewProvidersComponent,
    TabContactsComponent,
    LinkGroupsComponent,
    TabLinksComponent,
  ],
  imports: [
    NgxMaskModule.forRoot(),
    TranslateModule,
    FuseSharedModule,
    LeafletModule,
    LeafletMarkerClusterModule,
    NgxLeafletFullscreenModule,
    MatIconModule,
    MatToolbarModule,
    MatTabsModule,
    MatButtonModule,
    MatSelectModule,
    MatInputModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDividerModule,
    MatTooltipModule,
    MatMenuModule,
    MatProgressBarModule,
    MatRippleModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    LocationsGroupsComponentModule,
    SegmentsModule,
    CompaniesComponentModule,
    MaintenanceTypeModule,
    MaintenanceTypeSearchComponentModule,
    SelectWithSearchModule,
    DialogAlertModule,
    DisclaimerModule,
    FcPaginatorModule,
    AddressModule,
    FcOutlinedSelectModule,
    FcSegmentOutlinedSelectModule,
    FcCompanyOutlinedSelectModule,
    FcMaintenanceTypesOutlinedSelectModule,
    FcDeadlineContractOutlinedSelectModule,
    FcSegmentsInputModule,
    MapAddressModule,
    VideoInfoModalModule,
    FcTextInputModule,
    FcButtonModule,
    TreeViewModule,
    NgxSkeletonLoaderModule,
    ArchiveSectorDialogModule,
    ArchiveEnvironmentDialogModule,
    DialogLinkEquipmentModule,
    DirectivesModule
  ],
  exports: [
    DialogLocationsComponent
  ],
  providers: [
    LocationService,
    CustomMessagesService,
    QueryAddressGeocodeGQL,
    QueryAddressPostalCodeGQL,
    QueryCoordinatesGeocodeGQL,
    QueryAddressCoordinatesGQL,
  ],
})
export class DialogLocationsModule {}
